import { createTheme, responsiveFontSizes } from '@material-ui/core/styles';
import { PaletteOptions } from '@material-ui/core/styles/createPalette';

export const buildTheme = (palette: PaletteOptions) => {
  const controlBorderRadius = '8px';
  const dark = 'var(--dark)';
  const gray = 'var(--gray)';
  const white = 'var(--white)';
  const primary = 'var(--primary)';
  const extraLightGray2 = 'var(--extraLightGray2)';

  return responsiveFontSizes(
    createTheme({
      palette,
      typography: {
        fontFamily: 'Roboto',
        h4: {
          fontFamily: 'Roboto',
          fontWeight: 700,
          fontSize: '28px',
          color: dark,
        },
        subtitle2: {
          fontFamily: 'Roboto',
          fontWeight: 600,
          fontSize: '14px',
          color: dark,
        },
      },
      overrides: {
        MuiBackdrop: {
          root: {
            backgroundColor: 'rgba(var(--dark_rgb), 0.3)',
          },
        },
        MuiInputBase: {
          input: {
            height: '18px',
            '&::placeholder': {
              color: gray,
            },
          },
        },
        MuiInput: {
          underline: {
            "&:before": {
              borderBottom: `1px solid #d3d7db`
            }
          }
        },
        MuiSlider: {
          root: {
            color: white,
            height: 4,
          },
          thumb: {
            display: 'none',
            height: 6,
            width: 6,
            backgroundColor: white,
            marginTop: '-2px',
            marginLeft: '-3px',
            boxShadow: 'none',
            '&:focus, &:hover, &$active': {
              boxShadow: 'none',
            },
          },
          rail: {
            height: 4,
            borderRadius: 8,
          },
          track: {
            height: 4,
          },
        },
        MuiChip: {
          sizeSmall: {
            height: '24px',
            fontSize: '12px',
          },
          root: {
            height: '29px',
            padding: '0 1px',
            borderRadius: controlBorderRadius,
          },
        },
        MuiOutlinedInput: {
          root: {
            borderRadius: controlBorderRadius,
            '&:hover $notchedOutline': {
              borderColor: primary,
            },
            '&$focused $notchedOutline': {
              borderWidth: 1,
            },
          },
          notchedOutline: {
            borderRadius: controlBorderRadius,
            borderColor: 'var(--lightGray4)',
          },
          input: {
            padding: '11px 21px',
            fontSize: '14px',
            color: dark,
            borderRadius: controlBorderRadius,
            '&::placeholder': {
              color: gray,
              opacity: 1,
            },
          },
        },
        MuiFilledInput: {
          root: {
            backgroundColor: 'var(--extraLightGray)',
            borderRadius: controlBorderRadius,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          },
          input: {
            padding: '11px 21px',
            fontSize: '14px',
          },
        },
        MuiCheckbox: {
          root: {
            padding: 0,
            paddingTop: '4px',
          },
        },
        MuiFormControlLabel: {
          root: {
            marginLeft: 0,
          },
          label: {
            marginLeft: '13px',
            lineHeight: '1.92',
          },
        },
        MuiButton: {
          root: {
            padding: '6px 20px',
            borderRadius: controlBorderRadius,
          },
          label: {
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 600,
          },
          outlined: {
            border: `1px solid var(--lightGray2)`,
            padding: '7px 10px',
          },
          outlinedPrimary: {
            border: `1px solid var(--lightGray2)`,
            borderRadius: controlBorderRadius,
            boxShadow: 'none',
            backgroundColor: extraLightGray2,
            color: dark,
            '&:hover': {
              borderColor: primary,
              backgroundColor: primary,
              color: white,
              boxShadow: 'none',
            },
            '& .MuiButton-label': {
              fontSize: '14px',
              fontWeight: 400,
            },
          },
          outlinedSecondary: {
            border: `1px solid var(--lightGray2)`,
            borderRadius: controlBorderRadius,
            boxShadow: 'none',
            backgroundColor: extraLightGray2,
            color: dark,
            '&:hover': {
              borderColor: primary,
              backgroundColor: 'rgba(var(--primary_rgb), 0.05)',
              color: primary,
              boxShadow: 'none',
            },
            '&:disabled': {
              color: gray,
              borderColor: 'var(--lightGray2)',
              backgroundColor: extraLightGray2,
            },
            '&[aria-expanded="true"]': {
              color: primary,
            },
            '& .MuiButton-label': {
              fontSize: '14px',
              fontWeight: 400,
            },
          },
          outlinedSizeSmall: {
            padding: '2px 9px',
          },
          contained: {
            boxShadow: 'none',
            color: dark,
            backgroundColor: white,
            '&:hover': {
              backgroundColor: white,
            },
            '&:disabled': {
              color: gray,
              backgroundColor: 'var(--extraLightGray)',
            },
          },
          containedPrimary: {
            color: white,
          },
          containedSizeSmall: {
            padding: '3px 10px',
            '& .MuiButton-label': {
              fontSize: '14px',
            },
          },
        },
        MuiFormHelperText: {
          root: {
            lineHeight: 'normal',
          },
          contained: {
            marginLeft: 0,
            marginTop: '6px',
          },
        },
        MuiTooltip: {
          tooltip: {
            backgroundColor: dark,
          },
          arrow: {
            color: dark,
          },
        },
        MuiMenu: {
          list: {
            padding: '10px',
          },
          paper: {
            marginTop: '5px',
            boxShadow: '5px 5px 20px 0 rgba(var(--dark_rgb), 0.1);',
          },
        },
        MuiMenuItem: {
          root: {
            fontSize: '14px',
            lineHeight: '1.36',
            padding: '5px 10px',
            borderRadius: controlBorderRadius,
            '&:hover': {
              color: primary,
              backgroundColor: 'var(--primaryLight3)',
            },
            '&$selected': {
              color: primary,
              backgroundColor: 'var(--primaryLight3)',
            },
          },
        },
        MuiDialog: {
          paperFullScreen: {
            borderTopLeftRadius: controlBorderRadius,
            borderTopRightRadius: controlBorderRadius,
          },
        },
        MuiTab: {
          textColorInherit: {
            opacity: '1',
            fontSize: '12px',
          },
        },
      },
    }),
  );
};
